import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, UserRound } from "lucide-react";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { forgotPassword } from "../lib/customerApi";

const fmtErr = (d) => {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return d?.msg || "Something went wrong.";
};

export default function AccountAuth() {
  const { user, login, register } = useCustomerAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/account" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        const res = await forgotPassword(form.email);
        if (res.dev_reset_token) {
          toast.success("Email isn't live in preview — taking you to reset now.");
          navigate(`/account/reset?token=${res.dev_reset_token}`);
        } else {
          toast.success("If that email exists, a reset link is on its way.");
          setMode("login");
        }
        return;
      }
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
      toast.success(mode === "login" ? "Welcome back." : "Account created — welcome!");
      navigate("/account");
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const input = "w-full bg-transparent border-b border-[#3a3a3a] focus:border-gold py-3.5 text-white placeholder:text-zinc-600 outline-none transition-colors";
  const title = mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Forgot Password";

  return (
    <main className="min-h-screen grid place-items-center px-5 pt-24 pb-16" data-testid="account-auth-page">
      <div className="w-full max-w-md bg-[#121212] border border-[#27272a] p-8 sm:p-10">
        <div className="h-11 w-11 grid place-items-center border border-gold text-gold mb-6"><UserRound size={20} /></div>
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">[ Customer Account ]</span>
        <h1 className="font-display text-3xl font-semibold uppercase tracking-tight mt-3 mb-2">{title}</h1>
        <p className="text-zinc-500 text-sm mb-7">
          {mode === "forgot"
            ? "Enter your email and we'll send you a link to reset your password."
            : "Track your studio bookings and card service orders in one place."}
        </p>
        <form onSubmit={submit} className="space-y-6">
          {mode === "register" && (
            <input className={input} placeholder="Full name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="account-name" />
          )}
          <input type="email" className={input} placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="account-email" />
          {mode !== "forgot" && (
            <input type="password" className={input} placeholder="Password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="account-password" />
          )}
          <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-black font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 font-semibold hover:bg-[#d59e19] transition-colors disabled:opacity-60" data-testid="account-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : title}
          </button>
        </form>
        <div className="mt-6 flex flex-col gap-2">
          {mode !== "forgot" && (
            <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-sm text-zinc-400 hover:text-gold transition-colors text-left" data-testid="account-toggle-mode">
              {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
            </button>
          )}
          {mode === "login" && (
            <button onClick={() => setMode("forgot")} className="text-sm text-zinc-500 hover:text-gold transition-colors text-left" data-testid="account-forgot-link">
              Forgot your password?
            </button>
          )}
          {mode === "forgot" && (
            <button onClick={() => setMode("login")} className="text-sm text-zinc-400 hover:text-gold transition-colors text-left" data-testid="account-back-login">
              ← Back to sign in
            </button>
          )}
          <Link to="/" className="text-xs font-mono uppercase tracking-[0.12em] text-zinc-600 hover:text-white mt-2">← Back to site</Link>
        </div>
      </div>
    </main>
  );
}

